import React, { useEffect, useMemo, useState } from "react";
import {
  GameState, POWERS, POWER_ORDER, PowerId, TERRITORY_MAP, UnitId, UNITS, isAir, isLand, isSea,
  type AuthUser,
} from "@aa/shared";
import { Board } from "./Board.js";
import { Panel } from "./Panel.js";
import { Net } from "./net.js";
import { UnitSelectionPopup } from "./UnitSelectionPopup.js";

interface GameProps {
  net: Net;
  gameId: string;
  state: GameState;
  myPower: PowerId | null;
  authUser: AuthUser | null;
  error: string | null;
  notice: string | null;
  lastSaved: number | null;
  onLogout: () => void;
  onQuit: () => void;
}

export function Game({ net, gameId, state, myPower, authUser, error, notice, lastSaved, onLogout, onQuit }: GameProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const [moveSrc, setMoveSrc] = useState<string | null>(null);
  const [moveUnits, setMoveUnits] = useState<string[]>([]);
  const [showUnitPicker, setShowUnitPicker] = useState(false);
  const [pickerSource, setPickerSource] = useState<string | null>(null);
  const [retreatPending, setRetreatPending] = useState<string | null>(null);
  const [confirmingQuit, setConfirmingQuit] = useState(false);
  const [sideOpen, setSideOpen] = useState(true);
  const [now, setNow] = useState(Date.now());

  // Tick every 30s so the "saved X minutes ago" label stays fresh.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const reachable = useMemo<Set<string>>(() => {
    // During place phase: highlight territories where units can be deployed
    if (state.phase === "place" && myPower === state.activePower) {
      const pool = state.powers[myPower]?.producedThisTurn ?? [];
      if (pool.length === 0) return new Set();

      const hasSea = pool.some((o) => UNITS[o.unit]?.domain === "sea");
      const hasLand = pool.some((o) => {
        const d = UNITS[o.unit]?.domain;
        return d === "land" || d === "air";
      });
      const hasFactoryUnit = pool.some((o) => o.unit === "factory");

      const out = new Set<string>();

      // Find all land territories owned by this power that have a factory
      const factoryTerritories = Object.values(state.units)
        .filter((u) => u.unit === "factory" && u.owner === myPower)
        .map((u) => u.territory);

      if (hasLand) {
        if (hasFactoryUnit) {
          // Factories can be placed in any owned land territory (no existing factory needed)
          for (const [tid, ts] of Object.entries(state.territories)) {
            if (ts.owner === myPower && TERRITORY_MAP[tid]?.terrain === "land") out.add(tid);
          }
        }
        // Other land/air units require a factory
        for (const tid of factoryTerritories) {
          if (state.territories[tid]?.owner === myPower) out.add(tid);
        }
      }

      if (hasSea) {
        // Sea zones adjacent to any factory territory
        for (const tid of factoryTerritories) {
          if (state.territories[tid]?.owner !== myPower) continue;
          for (const n of TERRITORY_MAP[tid]?.neighbors ?? []) {
            if (TERRITORY_MAP[n]?.terrain === "sea") out.add(n);
          }
        }
      }

      return out;
    }

    if (!moveSrc) return new Set();
    const src = TERRITORY_MAP[moveSrc];
    if (!src) return new Set();
    const maxMove = moveUnits.length > 0
      ? Math.max(...moveUnits.map((id) => UNITS[state.units[id]?.unit ?? "infantry"].move))
      : 0;
    if (maxMove === 0) return new Set();

    const domains = new Set(moveUnits.map((id) => UNITS[state.units[id]?.unit ?? "infantry"].domain));
    const allLand = domains.size > 0 && [...domains].every((d) => d === "land");
    const allSea = domains.size > 0 && [...domains].every((d) => d === "sea");

    // For combat move, only highlight valid attack destinations (enemy territory/units)
    const isCombatMove = state.phase === "combatMove" && myPower != null;
    const isEnemyTerritory = (tid: string): boolean => {
      const tOwner = state.territories[tid]?.owner;
      const hasEnemyUnits = Object.values(state.units).some(
        (u) => u.territory === tid && POWERS[u.owner].alliance !== POWERS[myPower!].alliance,
      );
      return hasEnemyUnits || (tOwner != null && POWERS[tOwner].alliance !== POWERS[myPower!].alliance);
    };

    const out = new Set<string>();
    const q: [string, number][] = [[moveSrc, 0]];
    const seen = new Set<string>([moveSrc]);
    while (q.length) {
      const [id, d] = q.shift()!;
      if (d >= maxMove) continue;
      for (const n of TERRITORY_MAP[id]?.neighbors ?? []) {
        if (seen.has(n)) continue;
        const nTerrain = TERRITORY_MAP[n]?.terrain;
        if (allLand && nTerrain !== "land") continue;
        if (allSea && nTerrain !== "sea") continue;
        seen.add(n);
        // During combat move, only add enemy destinations; still traverse all to find paths
        if (!isCombatMove || isEnemyTerritory(n)) out.add(n);
        q.push([n, d + 1]);
      }
    }
    return out;
  }, [moveSrc, moveUnits, state.units, state.phase, state.activePower, myPower, state.powers, state.territories]);

  // During place phase, auto-select the only valid territory if there's exactly one
  useEffect(() => {
    if (state.phase === "place" && myPower === state.activePower) {
      if (reachable.size === 1) {
        setSelected([...reachable][0]);
      }
    }
  }, [state.phase, state.activePower, myPower, reachable]);

  function onRetreatRequest(battleTerritory: string) {
    setRetreatPending(battleTerritory);
  }

  function onTerritoryClick(id: string): void {
    if (retreatPending) {
      net.send({ type: "resolveBattle", gameId, territory: retreatPending, retreat: true, retreatTo: id });
      setRetreatPending(null);
      return;
    }
    setSelected(id);

    if (state.phase === "purchase" || state.phase === "collect" || state.phase === "place") return;

    if (state.phase === "combatMove" || state.phase === "nonCombatMove") {
      if (myPower !== state.activePower) return;

      if (!moveSrc) {
        const myUnits = Object.values(state.units).filter(u => u.territory === id && u.owner === myPower);
        if (myUnits.length > 0) {
          setPickerSource(id);
          setShowUnitPicker(true);
        }
      } else {
        if (id === moveSrc) { setMoveSrc(null); setMoveUnits([]); return; }
        const unitDomains = new Set(moveUnits.map((uid) => UNITS[state.units[uid]?.unit ?? "infantry"].domain));
        const pathTerrain = unitDomains.size > 0 && [...unitDomains].every((d) => d === "land") ? "land" as const
          : unitDomains.size > 0 && [...unitDomains].every((d) => d === "sea") ? "sea" as const
          : undefined;
        const path = bfsPath(moveSrc, id, pathTerrain);
        if (!path || moveUnits.length === 0) return;
        net.send({
          type: "moveOrder",
          gameId,
          unitIds: moveUnits,
          path,
          kind: state.phase === "combatMove" ? "combat" : "nonCombat",
        });
        setMoveSrc(null);
        setMoveUnits([]);
      }
    }
  }

  function onPickerConfirm(unitIds: string[]) {
    setShowUnitPicker(false);
    setMoveSrc(pickerSource!);
    setMoveUnits(unitIds);
    setPickerSource(null);
  }

  function onPickerCancel() {
    setShowUnitPicker(false);
    setPickerSource(null);
  }

  function onPurchase(orders: { unit: UnitId; qty: number }[]) {
    net.send({ type: "purchase", gameId, orders });
  }
  function onEndPhase() { net.send({ type: "endPhase", gameId }); }
  function onResolveBattle(territory: string, opts: { retreat?: boolean; retreatTo?: string }) {
    net.send({ type: "resolveBattle", gameId, territory, ...opts });
  }
  function onPlace(unit: UnitId, territory: string) {
    net.send({ type: "placeUnit", gameId, unit, territory });
  }

  function savedLabel(): string {
    if (!lastSaved) return "";
    const secs = Math.floor((now - lastSaved) / 1000);
    if (secs < 5) return "Saved";
    if (secs < 60) return `Saved ${secs}s ago`;
    return `Saved ${Math.floor(secs / 60)}m ago`;
  }

  return (
    <div className={"app" + (sideOpen ? "" : " side-collapsed")}>
      {/* Quit confirmation overlay */}
      {confirmingQuit && (
        <div className="quit-overlay">
          <div className="quit-dialog">
            <div className="quit-title">Forfeit game?</div>
            <div className="quit-body">
              {myPower
                ? `You will surrender as ${POWERS[myPower].name}. Your territories remain on the map — opponents can still attack them.`
                : "You will leave this game."}
            </div>
            <div className="quit-actions">
              <button className="btn" onClick={() => setConfirmingQuit(false)}>Cancel</button>
              <button
                className="btn danger"
                onClick={() => { setConfirmingQuit(false); onQuit(); }}
              >
                Yes, forfeit
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="board-wrap">
        <Board
          state={state}
          myPower={myPower}
          selectedTerritory={selected}
          setSelectedTerritory={setSelected}
          reachable={reachable}
          onTerritoryClick={onTerritoryClick}
        />
        <div className="phase-banner">
          <span className="power-chip" style={{ background: POWERS[state.activePower].color }}>
            <span className="dot" style={{ background: POWERS[state.activePower].accent }} />
            {POWERS[state.activePower].name}
          </span>
          <span className="phase-name">{state.phase}</span>
          {myPower && <span style={{ fontSize: 12, color: "#8aa0b5" }}>
            you: {POWERS[myPower].name} {myPower === state.activePower ? "— your move" : "— waiting"}
          </span>}
        </div>
        {moveSrc && (
          <div className="legend">
            Moving from <b>{TERRITORY_MAP[moveSrc].name}</b> · click destination ({moveUnits.length} units) — click origin again to cancel
          </div>
        )}
        {retreatPending && (
          <div className="legend">
            Retreat from <b>{TERRITORY_MAP[retreatPending]?.name ?? retreatPending}</b> · click destination territory to retreat
          </div>
        )}
        {error && <div className="toast toast-error">{error}</div>}
        {notice && !error && <div className="toast toast-notice">{notice}</div>}
        {state.winner && (
          <div className="toast toast-victory">
            {state.winner === "axis" ? "AXIS VICTORY" : "ALLIES VICTORY"}
          </div>
        )}
        {showUnitPicker && pickerSource && myPower && (
          <UnitSelectionPopup
            sourceId={pickerSource}
            units={Object.values(state.units).filter(u => u.territory === pickerSource && u.owner === myPower)}
            myPower={myPower}
            state={state}
            onConfirm={onPickerConfirm}
            onCancel={onPickerCancel}
          />
        )}
      </div>

      <div className="side">
        {/* Header always visible — toggle lives here so it never floats over the map */}
        <div className="side-header">
          <button
            className="side-toggle"
            onClick={() => setSideOpen(o => !o)}
            title={sideOpen ? "Collapse panel" : "Expand panel"}
          >
            {sideOpen ? "▶" : "◀"}
          </button>
          {sideOpen && (
            <>
              <div className="side-header-left">
                <span className="side-game-id">#{gameId}</span>
                <span className="side-save">{savedLabel()}</span>
              </div>
              <div className="side-header-right">
                {authUser && (
                  <span className="side-user" title={authUser.email}>
                    {authUser.displayName}
                  </span>
                )}
                <button className="side-icon-btn" onClick={onLogout} title="Log out">⏏</button>
                {!state.winner && (
                  <button
                    className="side-icon-btn danger"
                    title="Forfeit and leave this game"
                    onClick={() => setConfirmingQuit(true)}
                  >
                    ✕
                  </button>
                )}
              </div>
            </>
          )}
        </div>

        {sideOpen && (
          <div className="side-body">
            <Panel
              state={state}
              myPower={myPower}
              selectedTerritory={selected}
              reachable={reachable}
              onPurchase={onPurchase}
              onEndPhase={onEndPhase}
              onResolveBattle={onResolveBattle}
              onPlace={onPlace}
              onRetreat={onRetreatRequest}
            />
            <div className="log">
              {[...state.log].reverse().slice(0, 80).map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** BFS through the territory graph with optional terrain constraint. */
function bfsPath(from: string, to: string, terrain?: "land" | "sea"): string[] | null {
  const prev: Record<string, string | null> = { [from]: null };
  const q = [from];
  while (q.length) {
    const id = q.shift()!;
    if (id === to) {
      const out: string[] = [];
      let cur: string | null = id;
      while (cur) { out.unshift(cur); cur = prev[cur]; }
      return out;
    }
    for (const n of TERRITORY_MAP[id]?.neighbors ?? []) {
      if (n in prev) continue;
      if (terrain === "land" && TERRITORY_MAP[n]?.terrain !== "land") continue;
      if (terrain === "sea" && TERRITORY_MAP[n]?.terrain !== "sea") continue;
      prev[n] = id;
      q.push(n);
    }
  }
  return null;
}
